import {
	HttpErrorResponse,
	HttpEvent,
	HttpEventType
}                     from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	FileDropErrorIdEnum,
	FileProcessConditionals,
	FileProcessStatusEnum,
	IFileDrop,
	IFileDropCallbacks,
	IFileDropConfig,
	IFileDropError,
	IFileDropGroupItem,
	IFileDropInitData,
	IFileDropInputConfig,
	IFileDropMaxFilesize,
	IFileDropTableColumnConfig
}                     from '@shared/components/file-drop/file-drop.model';

import {
	FileUtils,
	StringUtils
}                   from '@utils/helpers';
import {
	Observable,
	Subject
}                   from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable()
export class FileDropService {

	get uploadFullyCompleted$(): Observable<boolean> {
		return this._uploadFullyCompletedSubject.asObservable();
	}

	get fileDropConfig(): IFileDropConfig {
		return this._fileDropConfig;
	}

	get uploadSuceededFileDropList(): IFileDrop[] {
		return this._uploadSuceededFileDropList;
	}

	get fileProcessConditionals(): FileProcessConditionals {
		return this._fileProcessConditionals ??
			{
				importInProgress: false,
				readyForImport: false,
				showLoader: false,
				hideErrors: true
			};
	}

	get fileDropList(): IFileDrop[] {
		return this._fileDropList ?? [];
	}

	get doesFileDropsWithTooltipExist(): boolean {
		return this.fileDropList.some( ( fileDrop: IFileDrop ) =>
			fileDrop.process.errorTooltipsExist
		);
	}

	private _errorBaseTranslationPath: string;
	private _errorSupportedStatusCodes: number[];
	private _successSupportedStatusCodes?: number[];
	private _fileDropList: IFileDrop[];
	private _fileProcessConditionals: FileProcessConditionals;
	private _fileDropCallbacks: IFileDropCallbacks<IFileDrop, any>;
	private _fileDropConfig: IFileDropConfig;
	private _uploadSuceededFileDropList: IFileDrop[] = [];
	private _uploadFullyCompletedSubject: Subject<boolean> = new Subject<boolean>();

	private setUploadFullyCompleted( uploadFullyCompleted: boolean ): void {
		if ( this._uploadSuceededFileDropList?.length ) {
			this._uploadFullyCompletedSubject.next( uploadFullyCompleted );
		}
	}

	private checkUploadProgress() {
		this.fileProcessConditionals.importInProgress =
			this.fileDropList.some( ( fileDrop: IFileDrop ) => {
				return [
					FileProcessStatusEnum.STARTED,
					FileProcessStatusEnum.UPLOADING
				].includes( fileDrop.process.status );
			} );

		this.checkForExistingDeletableItems();

		if (
			this.fileDropList.length > 0 &&
			!this.fileProcessConditionals.importInProgress &&
			this.isImportProcessCompleted()
		) {
			this.setUploadFullyCompleted( true );
		}
	}

	private isImportProcessCompleted(): boolean {
		return this.fileDropList.every( ( fileDrop: IFileDrop ) =>
			fileDrop.process.markedAsUploaded ||
			fileDrop.process.uploadForbidden && fileDrop.process.status !== FileProcessStatusEnum.FAILED
		);
	}

	private checkForExistingDeletableItems(): void {
		this.fileProcessConditionals.deletableItemsExist =
			this.fileDropList.some( ( fileDrop: IFileDrop ) =>
				fileDrop.process.uploadForbidden || fileDrop.process.errorMap.size
			);
	}

	private areAllUploaded(): boolean {
		return this.fileDropList.every( ( fileDrop: IFileDrop ) => fileDrop.process.markedAsUploaded );
	}

	private areAllUploadForbidden(): boolean {
		return this.fileDropList.every( ( fileDrop: IFileDrop ) =>
			fileDrop.process.uploadForbidden
		);
	}

	private processUploadingFile(
		fileDrop: IFileDrop,
		httpEvent: HttpEvent<any>
	): void {
		switch ( httpEvent.type ) {
			case HttpEventType.Sent: // type 0
				fileDrop.process.uploadProgress = 0;
				fileDrop.process.status = FileProcessStatusEnum.UPLOADING;
				break;
			case HttpEventType.UploadProgress: // type 1
				fileDrop.process.uploadProgress = Math.round( httpEvent.loaded / httpEvent.total * 100 );
				break;
			case HttpEventType.ResponseHeader: // type 2
				break;
			case HttpEventType.Response: // type 4
				if ( this._successSupportedStatusCodes?.includes( httpEvent.status ) ) {
					fileDrop.process.status = FileProcessStatusEnum.SUCCESS;
					fileDrop.process.markedAsUploaded = true;
					fileDrop.entity = Array.isArray( httpEvent.body ) ? httpEvent.body[ 0 ] : httpEvent.body;

					if ( this._fileDropCallbacks?.fileUploadSuccessfulFinishedCallback ) {
						this._fileDropCallbacks?.fileUploadSuccessfulFinishedCallback( fileDrop );
					}

					this._uploadSuceededFileDropList.push( {
						...fileDrop,
						metadata: null
					} );
				}
				break;
		}
	}

	private resetFileProcessConditionals(): void {
		this._fileProcessConditionals = {
			importInProgress: false,
			readyForImport: false,
			showLoader: false,
			hideErrors: true
		};
	}

	private processUploadingFileError(
		fileDrop: IFileDrop,
		httpErrorResponse: HttpErrorResponse
	): void {

		// by status code
		const statusCode = httpErrorResponse.status;
		let errorTranslationPath = `${ this._errorBaseTranslationPath }.text`;
		if ( statusCode && this._errorSupportedStatusCodes.includes( statusCode ) ) {
			errorTranslationPath += `-${ statusCode }`;
		}

		// by error code
		const errorCode = httpErrorResponse.error?.code;
		if ( errorCode ) {
			errorTranslationPath = `${ this._errorBaseTranslationPath }.${ StringUtils.toCamelCase( errorCode ) }`;
		}

		fileDrop.process.status = FileProcessStatusEnum.FAILED;

		const uploadError: IFileDropError = {
			id: FileDropErrorIdEnum.API,
			long: `${ errorTranslationPath }.long`,
			short: `${ errorTranslationPath }.short`
		};

		this.addFileDropError( fileDrop, uploadError );
	}

	private uploadFile( fileDrop: IFileDrop ): void {
		if ( !this._fileDropCallbacks?.uploadRequestCallback ) { return; }

		this._fileDropCallbacks?.uploadRequestCallback( fileDrop )
		.pipe(
			finalize( () => this.checkUploadProgress() )
		)
		.subscribe( {
			next: ( httpEvent: HttpEvent<any> ) => {
				this.processUploadingFile( fileDrop, httpEvent );
			},
			error: ( error: HttpErrorResponse ) => {
				this.processUploadingFileError( fileDrop, error );
			}
		} );
	}

	private sortFileDropList( fileDropList: IFileDrop[] = [] ): IFileDrop[] {
		// sort by file name
		const sortedFilenameList = fileDropList.sort( ( fileDropA: IFileDrop, fileDropB: IFileDrop ) =>
			fileDropA.fileData.file.name.localeCompare( fileDropB.fileData.file.name )
		);

		let sortedFileDropList: IFileDrop[] = [];

		// group sorting
		if ( this.fileDropConfig?.table?.group?.showGroups ) {
			// build sorted groupItems
			const groupItems: IFileDropGroupItem[] = this.fileDropConfig?.table?.group?.items;
			const sortedGroupItems: IFileDropGroupItem[] = groupItems?.sort( ( groupIdA: IFileDropGroupItem, groupIdB: IFileDropGroupItem ) =>
				groupIdA.order - groupIdB.order
			);

			// builds sorted fileDropList by group order
			for ( const groupItem of sortedGroupItems ) {
				// filter files with same groupId
				const filteredFileDropList = fileDropList.filter( ( fileDrop: IFileDrop ) =>
					fileDrop.groupId === groupItem.id
				);

				// adds metadata to filtered group list fileDropItems
				filteredFileDropList.forEach( ( fileDrop: IFileDrop, index: number ) => {
					Object.assign(
						fileDrop,
						{
							...fileDrop,
							metadata: {
								...fileDrop.metadata,
								groupsEntryIndex: index,
								isGroupsFirstEntry: index === 0,
								isGroupsLastEntry: index === fileDrop.metadata.groupsTotalEntries - 1
							}
						}
					);
				} );

				sortedFileDropList.push( ...filteredFileDropList );
			}
		} else { // no group sorting
			sortedFileDropList = sortedFilenameList;
		}

		return sortedFileDropList;
	}


	private doesFileExistInFileDropList( file: File ): boolean {
		return this.fileDropList.some( ( fileDrop: IFileDrop ) => fileDrop.fileData.fullname === file.name );
	}

	private setFileDropList( fileDropList: IFileDrop[] ): void {
		this._fileDropList = fileDropList;
	}

	private sortConfigsTableColumnItemsByOrder( tableColumnItemsConfig: IFileDropTableColumnConfig[] ): IFileDropTableColumnConfig[] {
		return tableColumnItemsConfig?.sort( ( tableColumnA: IFileDropTableColumnConfig, tableColumnB: IFileDropTableColumnConfig ) =>
			tableColumnA.order - tableColumnB.order
		);
	}

	private doesTooltipErrorsExist( fileDrop: IFileDrop ): boolean {
		return this.getErrors( fileDrop ).some( ( fileDropError: IFileDropError ) => fileDropError.long?.length );
	}

	private validateFileDrop(
		fileDrop: IFileDrop,
		fileDropMaxFilesize: IFileDropMaxFilesize,
		allowedFilesizeExceeded: boolean
	): IFileDrop {

		// Filesize
		if ( allowedFilesizeExceeded ) {
			const uploadError: IFileDropError = {
				id: FileDropErrorIdEnum.MAX_FILESIZE,
				long: `shared.fileDrop.errors.maxFilesize.long`,
				short: `shared.fileDrop.errors.maxFilesize.short`,
				params: {
					size: fileDropMaxFilesize.value,
					unit: fileDropMaxFilesize.unit
				}
			};

			this.addFileDropError( fileDrop, uploadError );
		}

		// Custom
		if ( this._fileDropCallbacks?.validateFileDrop ) {
			fileDrop = this._fileDropCallbacks?.validateFileDrop( fileDrop );
		}

		return fileDrop;
	}

	getFileDropInputConfigByGroupId( groupId: string ): IFileDropInputConfig {
		return this.fileDropConfig?.inputs?.find( ( fileDropInputConfig: IFileDropInputConfig ) =>
			fileDropInputConfig?.groupId === groupId
		);
	}

	init<T extends IFileDrop, Entity>( initData: IFileDropInitData<IFileDrop, any> ): void {
		this._errorBaseTranslationPath = initData?.errorBaseTranslationPath;
		this._errorSupportedStatusCodes = initData?.errorSupportedStatusCodes;
		this._fileDropCallbacks = initData?.fileDropCallbacks;
		this._successSupportedStatusCodes = initData?.successSupportedStatusCodes ?? [ 200, 201, 202 ];

		this._fileDropConfig = {
			...initData?.fileDropConfig,
			table: {
				...initData?.fileDropConfig?.table,
				tableColumnItemsConfig: this.sortConfigsTableColumnItemsByOrder( initData?.fileDropConfig?.table?.tableColumnItemsConfig )
			}
		};
	}

	import() {
		if ( this._fileDropCallbacks.beforeUploadCheckCallback ) {
			const hasCheckPassed: boolean = this._fileDropCallbacks.beforeUploadCheckCallback( this.fileDropList );
			if ( !hasCheckPassed ) {
				this._fileProcessConditionals.hideErrors = false;
				return;
			} else {
				this._fileProcessConditionals.hideErrors = true;
			}
		}

		this.fileDropList.forEach( ( fileDrop: IFileDrop ) => {
			if ( !fileDrop.process.markedAsUploaded && !fileDrop.process.uploadForbidden ) {
				if ( !this.fileProcessConditionals?.importInProgress ) { this.fileProcessConditionals.importInProgress = true; }
				Object.assign(
					fileDrop,
					{
						...fileDrop,
						process: {
							...fileDrop.process,
							status: FileProcessStatusEnum.STARTED
						}
					}
				);
				this.uploadFile( fileDrop );
			}
		} );
	}

	buildImportDataList( files: File[] | FileList, fileDropInputConfig: IFileDropInputConfig ): void {
		files = files ?? [];
		const fileDropMaxFilesize = fileDropInputConfig?.maxFilesize;

		// convert to array if files comes from input and not dragAndDrop Directive
		if ( files instanceof FileList ) { files = Array.from( files ); }

		// build fileDrop
		if ( files.length ) {
			// reset
			this.resetFileProcessConditionals();

			const groupsTotalEntries: number = files.length;
			let fileDropList: IFileDrop[] = [];

			// transfer FileList for additional options like: delete, upload status & settings, table attributes
			files.forEach( file => {

				// blocks adding existing files
				if ( this.doesFileExistInFileDropList( file ) ) { return; }

				const lastDotIndex: number = file.name.lastIndexOf( '.' );
				const extension = file.name.slice( lastDotIndex ).replace( '.', '' ).toLowerCase();
				const name = file.name.slice( 0, lastDotIndex );

				const allowedFilesizeExceeded =
					fileDropMaxFilesize
					? FileUtils.isFilesizeAboveLimit(
						file.size,
						fileDropMaxFilesize.value,
						fileDropMaxFilesize.unit
					)
					: null;

				// create default fileDrop
				let fileDrop: IFileDrop = {
					entity: null,
					fileData: {
						file,
						fullname: file?.name,
						name,
						extension,
						prettyFilesize: FileUtils.getPrettyFilesize( file.size ),
						allowedFilesizeExceeded
					},
					groupId: fileDropInputConfig?.groupId,
					metadata: {
						groupsTotalEntries,
						groupsEntryIndex: null,
						isGroupsFirstEntry: null,
						isGroupsLastEntry: null
					},
					process: {
						markedAsUploaded: false,
						status: FileProcessStatusEnum.NOT_ASKED,
						errorMap: new Map<FileDropErrorIdEnum | string, IFileDropError>(),
						errorLabel: null,
						errorTooltipsExist: false,
						uploadForbidden: false,
						uploadProgress: 0
					}
				};

				// customize default fileDrop
				fileDrop = this._fileDropCallbacks?.buildFileDropMapCallback
									 ? this._fileDropCallbacks?.buildFileDropMapCallback( fileDrop )
									 : fileDrop;

				// validate build fileDrop
				fileDrop = this.validateFileDrop( fileDrop, fileDropMaxFilesize, allowedFilesizeExceeded );

				// add fileDrop to list
				fileDropList.push( fileDrop );
			} );


			// stops further processing if no new files exists, because of existing file check block
			if ( !fileDropList.length ) { return; }

			let groupIdCleanedFileDropList: IFileDrop[] = [];

			// and keeps existing and adds new files
			if ( this.fileDropConfig?.keepExistingFileDropListOnNewDrops ) {
				fileDropList.push( ...this.fileDropList );
			} else {
				// removes old file items with matching groupId and keeps other groupIds files
				groupIdCleanedFileDropList = this.fileDropList.filter( ( fileDrop: IFileDrop ) =>
					fileDrop.groupId !== fileDropInputConfig?.groupId
				);
			}

			// multisorts file items by group and filename
			fileDropList = this.sortFileDropList( [ ...fileDropList, ...groupIdCleanedFileDropList ] );

			this.setFileDropList( fileDropList );
			this.checkForExistingDeletableItems();
			this.fulfillsUploadRequirements();
		}
	}

	getErrors( fileDrop: IFileDrop ): IFileDropError[] {
		return Array.from( fileDrop?.process?.errorMap?.entries() )
		.map( ( arrayMap: [ string, IFileDropError ] ) => arrayMap[ 1 ] );
	}

	addFileDropError(
		fileDrop: IFileDrop,
		error: IFileDropError
	): void {
		fileDrop.process.errorMap.set( error.id, error );

		Object.assign(
			fileDrop,
			{
				...fileDrop,
				process: {
					...fileDrop.process,
					errorLabel: fileDrop.process.errorMap.size > 1
											? 'shared.fileDrop.multipleErrors'
											: error.short,
					errorTooltipsExist: this.doesTooltipErrorsExist( fileDrop ),
					uploadForbidden: true
				}
			}
		);
	}

	removeFileDropError(
		fileDrop: IFileDrop,
		errorId: FileDropErrorIdEnum | string
	): void {
		fileDrop.process.errorMap.delete( errorId );

		if ( !fileDrop.process.errorMap.size ) {
			Object.assign(
				fileDrop,
				{
					...fileDrop,
					process: {
						...fileDrop.process,
						errorLabel: fileDrop.process.errorMap.size === 0
												? null
												: fileDrop.process.errorMap.size > 1
													? 'shared.fileDrop.multipleErrors'
													: this.getErrors( fileDrop )[ 0 ].short,
						errorTooltipsExist: this.doesTooltipErrorsExist( fileDrop ),
						uploadForbidden: fileDrop.process.errorMap.size
					}
				}
			);
		}
	}

	fulfillsUploadRequirements(): void {
		let customFulfillsUploadRequirements = true;
		if ( this._fileDropCallbacks?.fulfillsUploadRequirementsCallback ) {
			customFulfillsUploadRequirements = this._fileDropCallbacks?.fulfillsUploadRequirementsCallback( this.fileDropList );
		}

		this.fileProcessConditionals.readyForImport = (
			!this.fileProcessConditionals.importInProgress &&
			this.fileDropList.length > 0 &&
			!this.areAllUploaded() &&
			!this.areAllUploadForbidden() &&
			customFulfillsUploadRequirements
		);
	}

	markFileDropItemAsDeleted( fileDropItemIndex: number ): void {
		this.fileDropList.splice( fileDropItemIndex, 1 );
		this.fulfillsUploadRequirements();
		this.checkUploadProgress();
	}

	deleteInvalidItems(): void {
		const cleanedFileDropList: IFileDrop[] = [];

		this.fileDropList.forEach( ( fileDrop: IFileDrop ) => {
			if ( !fileDrop.process.uploadForbidden && !fileDrop.process.errorMap.size ) {
				cleanedFileDropList.push( fileDrop );
			}
		} );

		this.setFileDropList( cleanedFileDropList );
		this.checkUploadProgress();
	}
}
